/* eslint-disable camelcase, class-methods-use-this */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { _ } from 'utils/i18n';

import { INTERRUPTION_START, INTERRUPTION_END } from 'scripts/frontend/lessons/interruption';

// Fixed duration the flanker stimuli are shown. The stimuli are always
// displayed for the full duration, even if the participant responds earlier.
const STIMULUS_DURATION_MS = 200;

// Maximum time the participant may take to respond after the stimulus
// disappeared. When the deadline passes without a response, the trial is
// recorded as a miss and the task advances automatically. This ensures that
// participants cannot influence the timing of the task by delaying their
// response.
const RESPONSE_WINDOW_MS = 1000;

// Seconds each probe question is shown and length of the rest period between
// blocks, per the task specification.
const PROBE_SECONDS = 8;
const REST_SECONDS = 10;

const TOTAL_BLOCKS = 3;

// The instruction screens advance on a tap anywhere, but they also scroll when
// the text does not fit. A pointer that travelled further than this, or that
// was released while the screen was still scrolling, is a scroll gesture and
// must not skip the screen.
const TAP_SLOP_PX = 10;
const SCROLL_SETTLE_MS = 400;

// The lesson's "abort" link is rendered into the page header at z-index 9999,
// so it floats above this task's fullscreen layer. Text screens reserve its
// height at the top so no text can end up underneath it. Clamped in case the
// header ever grows unexpectedly.
const MAX_HEADER_INSET_PX = 96;

// Stages driven by a timer, which therefore have to be paused while a dialog
// or the rotation notice covers the screen.
const PAUSABLE_STAGES = ['practice', 'main', 'probes', 'practice_probes', 'rest'];

const IntroSequenceText = _('In the following task, you will complete a series of trials. Each trial begins with a fixation cross displayed at the center of the screen, followed by a row of five arrows.');
const IntroFocusText = _('Your task is to focus only on the middle arrow and ignore the arrows surrounding it.');
// Emphasised within `IntroFocusText`, so its translation has to use the exact
// same wording as the translation of that sentence.
const IntroFocusEmphasis = _('middle arrow');
const IntroButtonsText = _('If the middle arrow points to the right, press the right button. If the middle arrow points to the left, press the left button.');
const IntroDifficultyText = _('Some trials will be easier because all arrows point in the same direction, while others will be more difficult because the surrounding arrows point in the opposite direction. The surrounding arrows are irrelevant to the decision.');
const IntroSpeedText = _('Please respond as quickly and accurately as possible.');
const IntroContinuePrompt = _('Touch the screen to continue');

const PracticeIntroText = _('Before starting the main task, you will complete a short practice round to familiarize yourself with the task and the response buttons.');
const PracticeCheckText = _('Please use this practice session to make sure you understand the instructions and feel comfortable responding to the arrows.');
const PracticeStartPrompt = _('When you are ready, touch the screen to begin the practice round.');

const MainIntroText = _('You will now begin the main task.');
const MainRemindText = _('Remember to focus only on the middle arrow and ignore the surrounding arrows. Respond as quickly and accurately as possible throughout the task.');
const MainProbesText = _('At the end of each block, you will also be asked a few short questions about your attention and performance during the task.');
const MainStartPrompt = _('When you are ready, touch the screen to begin the main task.');

const RestHeading = _('Rest');
const RestText = _('Please take a short break.');
const RestCountdownText = _('The next block will start in %(seconds)s seconds.');

const ProbeQuestions = [
    _('Were you focused on the task?'),
    _('Were you thinking of something else?'),
    _('How confident were you of the accuracy of your performance during the last block?'),
];
// The handle colours of the questionnaire sliders: $mpg-yellow, and
// lighten($mpg-yellow, 20%) while the participant has not answered yet.
const SLIDER_THUMB_COLOR = '#eeae39';
const SLIDER_THUMB_COLOR_UNTOUCHED = '#f6d497';

// Pressed state of the two response buttons. Android draws its own tap
// highlight on a touchable element, iOS does not, so the task draws the
// feedback itself on both platforms and turns the native highlight off.
const RESPONSE_BUTTON_COLOR = '#333';
const RESPONSE_BUTTON_COLOR_PRESSED = '#1e88e5';
const RESPONSE_FEEDBACK_MS = 150;

const ProbeScaleMinLabel = _('Not at all');
const ProbeScaleMaxLabel = _('Completely');
const ProbeConfirmLabel = _('Confirm');

const CompletedHeading = _('Task Completed');
const CompletedText = _('Thank you for completing the Flanker task.');
const CompletedContinueLabel = _('Continue');

const RotateHeading = _('Please rotate your device');
const RotateText = _('This task requires landscape (horizontal) mode. Please turn your phone sideways to continue.');

const IncompleteWarning = _('Please complete the Flanker task first.');

/**
 * Renders `text` with the first occurrence of `term` wrapped in <strong>. This
 * keeps the emphasis of a sentence without splitting it into fragments that
 * translators cannot reorder, and degrades to plain text when the term does
 * not appear in the translation.
 */
const emphasize = (text, term) => {
    const index = term ? text.indexOf(term) : -1;

    if (index < 0) return text;

    return (
        <React.Fragment>
            {text.slice(0, index)}
            <strong>{term}</strong>
            {text.slice(index + term.length)}
        </React.Fragment>
    );
};

const styles = {
    responseButton: {
        flex: 1,
        borderRadius: '10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#fff',
        fontSize: '40px',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // Android would otherwise add its own tap highlight on top of the
        // pressed state the task draws, which is what made the two platforms
        // look different in the first place.
        WebkitTapHighlightColor: 'transparent',
        transition: 'background-color 60ms linear',
        touchAction: 'manipulation',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        width: '100%',
        backgroundColor: '#000', // Changed to black to match slide backgrounds
        color: '#fff',
        fontFamily: 'Roboto, sans-serif',
        padding: '20px',
        textAlign: 'center',
        boxSizing: 'border-box',
    },
    fullscreen: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000',
        color: '#fff',
        fontFamily: 'Roboto, sans-serif',
        padding: '20px',
        textAlign: 'center',
        userSelect: 'none',
        zIndex: 1000,
        boxSizing: 'border-box',
    },
    heading: {
        fontSize: '28px',
        fontWeight: 'bold',
        marginBottom: '20px',
        color: 'inherit',
    },
    text: {
        fontSize: '18px',
        marginBottom: '15px',
        maxWidth: '600px',
        color: 'inherit',
    },
    btn: {
        marginTop: '30px',
        padding: '10px 30px',
        fontSize: '18px',
    },
    fullscreenTap: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000',
        color: '#fff',
        fontFamily: 'Roboto, sans-serif',
        padding: '40px',
        textAlign: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        zIndex: 1000,
        boxSizing: 'border-box',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    content: {
        maxWidth: '650px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        // Center the content vertically when it fits on the screen while
        // keeping it reachable (scrollable) from the top when it does not.
        marginTop: 'auto',
        marginBottom: 'auto',
        flexShrink: 0,
    },
    instructionText: {
        fontSize: '18px',
        lineHeight: '1.6',
        margin: 0,
        color: '#ccc',
        textAlign: 'left',
        width: '100%',
    },
    tapPrompt: {
        fontSize: '20px',
        fontWeight: 'bold',
        marginTop: '30px',
        color: '#fff',
        animation: 'flankerPulse 2s infinite',
    },
};

// Scrollable variant of `fullscreen` for text-heavy screens (instructions,
// probes, rest and completion screens). The content is vertically centered
// when it fits and can be scrolled from the top when it does not, so no text
// is ever cut off on small (landscape phone) screens.
styles.fullscreenScroll = {
    ...styles.fullscreen,
    justifyContent: 'flex-start',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
};

export default class Flanker extends Component {
    static propTypes = {
        block: PropTypes.object.isRequired,
        onContinue: PropTypes.func.isRequired,
        updateAnswer: PropTypes.func.isRequired,
        answer: PropTypes.object,
    };

    static canContinue(answer) {
        // Return falsy when the task is complete (no missing info), truthy string when blocked
        if (answer && typeof answer === 'string') {
            try {
                const data = JSON.parse(answer);
                if (data.main_trials && data.main_trials.length > 0) {
                    return false; // Can continue — task is done
                }
            } catch (e) { /* ignore parse errors */ }
        }
        return IncompleteWarning;
    }

    constructor(props) {
        super(props);
        this.state = {
            stage: 'intro', // intro, practice_intro, practice, practice_probes, main_intro, main, rest, probes, completed
            trialIndex: 0,
            practiceTrials: [],
            mainTrials: [],
            probes: [],
            blockNr: 1, // 1 to 3
            currentTrialPhase: null, // fixation, stimulus, response
            probeIndex: 0,

            // recorded answers
            recordedPractice: [],
            recordedMain: [],
            recordedProbes: [],
            recordedPracticeProbes: [], // track practice probe responses

            countdown: REST_SECONDS, // for rest
            timerLeft: PROBE_SECONDS, // for probes
            sliderValue: 3, // default slider value (middle of 0-6)
            sliderTouched: false, // track whether the user interacted with the slider
            pressedResponse: null, // 'left' or 'right' while the button flashes
            currentProbeAnswers: {}, // temp store for the 3 probe answers of a block

            isPortrait: this.checkIsPortrait(), // orientation lock
            abortDialogOpen: false,
            paused: false,
            headerInset: 0,
        };

        this.fixationTimeout = null;
        this.stimulusTimeout = null;
        this.responseTimeout = null;
        this.probeInterval = null;
        this.restInterval = null;
        // Set synchronously per trial, so rapid/double responses cannot be
        // recorded twice for the same trial.
        this.trialResponded = false;
        // Whether resuming should continue with the next trial rather than
        // repeat the interrupted one (see `pauseTask`).
        this.resumeWithNextTrial = false;
        this.tapOrigin = null;
        this.lastScrollAt = 0;
        this.headerMeasureFrame = null;
        this.unmounted = false;
    }

    checkIsPortrait() {
        if (typeof window !== 'undefined') {
            return window.innerHeight > window.innerWidth;
        }
        return false;
    }

    handleOrientationChange = () => {
        const isPortrait = this.checkIsPortrait();
        const headerInset = this.measureHeaderInset();

        if (isPortrait === this.state.isPortrait) {
            this.setState({ headerInset });
            return;
        }

        // Rotating away from landscape hides the task behind the rotation
        // notice, so the running trial has to stop rather than elapse unseen.
        this.setState({ isPortrait, headerInset }, this.syncPausedState);
    }

    handleInterruptionStart = () => {
        if (this.state.abortDialogOpen) return;

        this.setState({ abortDialogOpen: true }, this.syncPausedState);
    }

    handleInterruptionEnd = () => {
        if (!this.state.abortDialogOpen) return;

        this.setState({ abortDialogOpen: false }, this.syncPausedState);
    }

    componentDidMount() {
        this.generateTrials();
        // Keyboard support for Left (ArrowLeft) and Right (ArrowRight)
        window.addEventListener('keydown', this.handleKeyDown);
        // Orientation detection for landscape lock
        window.addEventListener('resize', this.handleOrientationChange);
        if (screen.orientation) {
            screen.orientation.addEventListener('change', this.handleOrientationChange);
        }
        window.addEventListener(INTERRUPTION_START, this.handleInterruptionStart);
        window.addEventListener(INTERRUPTION_END, this.handleInterruptionEnd);

        // Measured on the next frame: the abort link is rendered into the page
        // header through a portal by a sibling component, so it is not
        // guaranteed to be in the DOM yet at this point.
        this.headerMeasureFrame = window.requestAnimationFrame(() => {
            this.headerMeasureFrame = null;
            this.setState({ headerInset: this.measureHeaderInset() });
        });
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.clearAllTimers();
        if (this.headerMeasureFrame) window.cancelAnimationFrame(this.headerMeasureFrame);
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('resize', this.handleOrientationChange);
        if (screen.orientation) {
            screen.orientation.removeEventListener('change', this.handleOrientationChange);
        }
        window.removeEventListener(INTERRUPTION_START, this.handleInterruptionStart);
        window.removeEventListener(INTERRUPTION_END, this.handleInterruptionEnd);
    }

    /**
     * Height taken up by the lesson's abort link, which the page header renders
     * above this task's fullscreen layer. Returns 0 when the link is absent.
     */
    measureHeaderInset() {
        if (typeof document === 'undefined') return 0;

        const header = document.querySelector('.lesson-abort-button') || document.getElementById('header-top-nav-container');

        if (!header) return 0;

        const { bottom } = header.getBoundingClientRect();

        return Math.max(0, Math.min(Math.ceil(bottom), MAX_HEADER_INSET_PX));
    }

    /**
     * Pauses or resumes the task so that it never runs while the participant
     * cannot see it — either because the abort dialog is open or because the
     * device is held in portrait and the rotation notice covers the screen.
     */
    syncPausedState = () => {
        const { isPortrait, abortDialogOpen, paused } = this.state;
        const interrupted = isPortrait || abortDialogOpen;

        if (interrupted && !paused) {
            this.pauseTask();
        } else if (!interrupted && paused) {
            this.resumeTask();
        }
    }

    pauseTask() {
        const { stage } = this.state;

        if (!PAUSABLE_STAGES.includes(stage)) return;

        this.clearAllTimers();

        // A trial that was already answered has its record stored, so the task
        // continues with the following trial. An unanswered one is repeated
        // from its fixation cross on resume, so that no trial is ever measured
        // across an interruption.
        this.resumeWithNextTrial = (stage === 'practice' || stage === 'main') && this.trialResponded;

        this.setState({ paused: true, currentTrialPhase: null, pressedResponse: null });
    }

    resumeTask() {
        const { stage } = this.state;

        this.setState({ paused: false }, () => {
            if (stage === 'practice' || stage === 'main') {
                if (this.resumeWithNextTrial) {
                    this.resumeWithNextTrial = false;
                    this.nextTrial();
                } else {
                    this.startTrial();
                }
            } else if (stage === 'probes' || stage === 'practice_probes') {
                // Give the participant the full time to read and answer again.
                this.setState({ timerLeft: PROBE_SECONDS }, this.startProbeTimer);
            } else if (stage === 'rest') {
                this.startRestCountdown();
            }
        });
    }

    handleKeyDown = e => {
        if (this.state.stage === 'practice' || this.state.stage === 'main') {
            if (this.state.currentTrialPhase === 'stimulus' || this.state.currentTrialPhase === 'response') {
                if (e.key === 'ArrowLeft') {
                    this.handleResponse('left');
                } else if (e.key === 'ArrowRight') {
                    this.handleResponse('right');
                }
            }
        }
    }

    clearTrialTimers() {
        if (this.fixationTimeout) clearTimeout(this.fixationTimeout);
        if (this.stimulusTimeout) clearTimeout(this.stimulusTimeout);
        if (this.responseTimeout) clearTimeout(this.responseTimeout);
        this.fixationTimeout = null;
        this.stimulusTimeout = null;
        this.responseTimeout = null;
    }

    clearFeedbackTimer() {
        if (this.feedbackTimeout) {
            clearTimeout(this.feedbackTimeout);
            this.feedbackTimeout = null;
        }
    }

    clearAllTimers() {
        this.clearTrialTimers();
        this.clearFeedbackTimer();
        if (this.probeInterval) clearInterval(this.probeInterval);
        if (this.restInterval) clearInterval(this.restInterval);
        this.probeInterval = null;
        this.restInterval = null;
    }

    /**
     * Shows the pressed state of a response button for a moment.
     *
     * Driven by a timer rather than by pointerup: a lifted finger is not
     * always reported (iOS drops it when the element re-renders), and the
     * feedback has to be visible even for a very short tap.
     */
    flashResponseButton = side => {
        this.clearFeedbackTimer();

        this.setState({ pressedResponse: side });

        this.feedbackTimeout = setTimeout(() => {
            this.feedbackTimeout = null;
            if (!this.unmounted) {
                this.setState({ pressedResponse: null });
            }
        }, RESPONSE_FEEDBACK_MS);
    };

    generateTrials() {
        // Generate practice trials: 30 trials (15 congruent, 15 incongruent)
        const practiceTrials = this.createBlock(30);
        // Generate main trials blocks: 3 blocks x 80 trials each
        const mainTrials1 = this.createBlock(80);
        const mainTrials2 = this.createBlock(80);
        const mainTrials3 = this.createBlock(80);

        this.setState({
            practiceTrials,
            mainTrials: [mainTrials1, mainTrials2, mainTrials3],
        });
    }

    createBlock(count) {
        const trials = [];
        const half = count / 2;

        // Congruent
        for (let i = 0; i < half; i++) {
            const targetDirection = (i % 2 === 0) ? 'left' : 'right';
            trials.push({
                condition: 'congruent',
                target_direction: targetDirection,
                correct_response: targetDirection,
                fixation_ms: this.getRandomFixation(),
            });
        }

        // Incongruent
        for (let i = 0; i < half; i++) {
            const targetDirection = (i % 2 === 0) ? 'left' : 'right';
            trials.push({
                condition: 'incongruent',
                target_direction: targetDirection,
                correct_response: targetDirection,
                fixation_ms: this.getRandomFixation(),
            });
        }

        return this.shuffle(trials);
    }

    getRandomFixation() {
        const fixations = [200, 500, 800];
        return fixations[Math.floor(Math.random() * fixations.length)];
    }

    shuffle(array) {
        const newArr = [...array];
        for (let i = newArr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
        }
        return newArr;
    }

    startPractice = () => {
        this.setState({ stage: 'practice', trialIndex: 0 }, this.startTrial);
    }

    startMain = () => {
        this.setState({ stage: 'main', trialIndex: 0, blockNr: 1 }, this.startTrial);
    }

    startTrial = () => {
        const { stage, trialIndex, blockNr, practiceTrials, mainTrials } = this.state;
        let trial;
        if (stage === 'practice') {
            trial = practiceTrials[trialIndex];
        } else if (stage === 'main') {
            trial = mainTrials[blockNr - 1][trialIndex];
        }

        if (!trial) return;
        if (this.state.paused) return;

        // Defend against orphaned timers of a previous trial so that the
        // fixation -> stimulus sequence can never be corrupted.
        // We only clear trial sequence timers here, allowing the visual feedback
        // flash on the response button to complete its brief duration without
        // sticking in the blue state.
        this.clearTrialTimers();
        this.trialResponded = false;

        const nextState = {
            currentTrialPhase: 'fixation',
            stimulusShownAt: null,
            currentTrial: trial,
        };
        if (!this.feedbackTimeout && this.state.pressedResponse) {
            nextState.pressedResponse = null;
        }

        // Every trial always starts with the fixation cross, shown for the
        // randomly assigned duration of this trial.
        this.setState(nextState);

        this.fixationTimeout = setTimeout(() => {
            this.setState({ currentTrialPhase: 'stimulus', stimulusShownAt: Date.now() });

            // The stimulus is displayed for the full fixed duration, even if
            // the participant has already responded.
            this.stimulusTimeout = setTimeout(() => {
                this.stimulusTimeout = null;
                if (this.trialResponded) {
                    this.nextTrial();
                } else {
                    this.setState({ currentTrialPhase: 'response' });
                    // Response deadline: the trial advances automatically, so
                    // the participant cannot influence the task timing by
                    // delaying (or omitting) the response.
                    this.responseTimeout = setTimeout(this.handleMissedResponse, RESPONSE_WINDOW_MS);
                }
            }, STIMULUS_DURATION_MS);
        }, trial.fixation_ms);
    }

    buildTrialRecord(participantResponse, RT) {
        const { stage, currentTrial, trialIndex, blockNr } = this.state;

        return {
            trial_nr: trialIndex + 1,
            condition: currentTrial.condition,
            target_direction: currentTrial.target_direction,
            correct_response: currentTrial.correct_response,
            participant_response: participantResponse,
            accuracy: participantResponse === currentTrial.correct_response ? 1 : 0,
            RT,
            fixation_ms: currentTrial.fixation_ms,
            block_nr: stage === 'practice' ? 0 : blockNr,
        };
    }

    saveTrialRecord(record, callback) {
        const { stage, recordedPractice, recordedMain } = this.state;

        if (stage === 'practice') {
            this.setState({ recordedPractice: [...recordedPractice, record] }, callback);
        } else {
            this.setState({ recordedMain: [...recordedMain, record] }, callback);
        }
    }

    handleResponse = participantResponse => {
        const {
            currentTrial,
            stimulusShownAt,
            currentTrialPhase,
        } = this.state;

        if (this.trialResponded) {
            return; // Only the first response of a trial counts.
        }
        if (this.state.paused) {
            return;
        }
        if (!stimulusShownAt || !currentTrial) {
            return;
        }
        if (currentTrialPhase !== 'stimulus' && currentTrialPhase !== 'response') {
            return;
        }

        this.flashResponseButton(participantResponse);
        this.trialResponded = true;
        if (this.responseTimeout) {
            clearTimeout(this.responseTimeout);
            this.responseTimeout = null;
        }

        const RT = Date.now() - stimulusShownAt;
        const record = this.buildTrialRecord(participantResponse, RT);

        // While the stimulus is still on screen it stays visible for its full
        // fixed duration; the stimulus timeout then advances to the next
        // trial. During the response window the trial advances immediately.
        const onRecorded = currentTrialPhase === 'response' ? this.nextTrial : undefined;

        this.saveTrialRecord(record, onRecorded);
    }

    handleMissedResponse = () => {
        this.responseTimeout = null;

        if (this.trialResponded) {
            this.nextTrial();
            return;
        }
        if (!this.state.currentTrial) return;

        this.trialResponded = true;
        this.clearFeedbackTimer();
        this.setState({ pressedResponse: null });

        // Record the trial as a miss so that every configured trial appears
        // in the data, even without a response.
        const record = this.buildTrialRecord('NA', 'NA');

        this.saveTrialRecord(record, this.nextTrial);
    }

    nextTrial = () => {
        const {
            stage,
            trialIndex,
            blockNr,
            practiceTrials,
            mainTrials,
        } = this.state;

        if (stage === 'practice') {
            if (trialIndex + 1 < practiceTrials.length) {
                this.setState({ trialIndex: trialIndex + 1 }, this.startTrial);
            } else {
                this.initProbes('practice_probes');
            }
        } else if (stage === 'main') {
            if (trialIndex + 1 < mainTrials[blockNr - 1].length) {
                this.setState({ trialIndex: trialIndex + 1 }, this.startTrial);
            } else {
                this.initProbes('probes');
            }
        }
    }

    initProbes = stageName => {
        this.clearAllTimers();
        this.setState({
            stage: stageName,
            pressedResponse: null,
            probeIndex: 0,
            timerLeft: PROBE_SECONDS,
            sliderValue: 3,
            sliderTouched: false,
            currentProbeAnswers: {},
        }, this.startProbeTimer);
    }

    startProbeTimer = () => {
        if (this.probeInterval) {
            clearInterval(this.probeInterval);
        }
        this.probeInterval = setInterval(() => {
            const { timerLeft, sliderValue, sliderTouched, paused } = this.state;
            if (paused) return;
            if (timerLeft <= 1) {
                this.handleProbeResponse(sliderTouched ? sliderValue : null);
            } else {
                this.setState({ timerLeft: timerLeft - 1 });
            }
        }, 1000);
    }

    handleProbeResponse = score => {
        clearInterval(this.probeInterval);
        const {
            stage,
            blockNr,
            probeIndex,
            recordedProbes,
            recordedPracticeProbes,
        } = this.state;

        const probeNames = ['Mindwandering_focus', 'Mindwandering_distracted', 'Metacognition'];
        const questionName = probeNames[probeIndex];

        this.setState(prevState => {
            const currentProbeAnswers = {
                ...prevState.currentProbeAnswers,
                [questionName]: score !== null ? score : 'NA',
            };
            return { currentProbeAnswers };
        }, () => {
            if (probeIndex + 1 < 3) {
                this.startProbesNextQuestion();
            } else {
                const { currentProbeAnswers } = this.state;
                const record = {
                    block_nr: stage === 'practice_probes' ? 0 : blockNr,
                    ...currentProbeAnswers,
                };

                if (stage === 'practice_probes') {
                    this.setState({
                        recordedPracticeProbes: [...recordedPracticeProbes, record],
                        currentProbeAnswers: {},
                    }, () => {
                        this.setState({ stage: 'main_intro' });
                    });
                } else {
                    this.setState({
                        recordedProbes: [...recordedProbes, record],
                        currentProbeAnswers: {},
                    }, () => {
                        this.finishBlock();
                    });
                }
            }
        });
    }

    startProbesNextQuestion = () => {
        this.setState(prevState => ({
            probeIndex: prevState.probeIndex + 1,
            timerLeft: PROBE_SECONDS,
            sliderValue: 3,
            sliderTouched: false,
        }), this.startProbeTimer);
    }

    finishBlock = () => {
        if (this.state.blockNr < TOTAL_BLOCKS) {
            this.setState({ stage: 'rest', countdown: REST_SECONDS, pressedResponse: null }, this.startRestCountdown);
        } else {
            this.finishTask();
        }
    }

    startRestCountdown = () => {
        if (this.restInterval) {
            clearInterval(this.restInterval);
        }
        this.restInterval = setInterval(() => {
            const { countdown, paused } = this.state;
            if (paused) return;
            if (countdown <= 1) {
                clearInterval(this.restInterval);
                this.restInterval = null;
                this.setState(prevState => ({
                    stage: 'main',
                    blockNr: prevState.blockNr + 1,
                    trialIndex: 0,
                }), this.startTrial);
            } else {
                this.setState({ countdown: countdown - 1 });
            }
        }, 1000);
    }

    finishTask = () => {
        const {
            recordedPractice,
            recordedMain,
            recordedProbes,
            recordedPracticeProbes,
        } = this.state;
        this.clearAllTimers();
        this.setState({ stage: 'completed', pressedResponse: null });

        const answerData = {
            practice_trials: recordedPractice,
            main_trials: recordedMain,
            probes: recordedProbes,
            practice_probes: recordedPracticeProbes,
        };

        this.props.updateAnswer(JSON.stringify(answerData));
    }

    renderStimulus(trial) {
        const condition = trial.condition;
        const targetDirection = trial.target_direction;
        let arrows = '';
        if (targetDirection === 'left') {
            arrows = condition === 'congruent' ? '<<<<<' : '>><>>';
        } else {
            arrows = condition === 'congruent' ? '>>>>>' : '<<><<';
        }

        return (
            <div style={{ fontSize: '10vh', letterSpacing: '10px', color: '#fff', textAlign: 'center', userSelect: 'none' }}>
                {arrows}
            </div>
        );
    }

    handleScreenScroll = () => {
        this.lastScrollAt = Date.now();
    }

    handleTapStart = e => {
        this.tapOrigin = {
            x: e.clientX,
            y: e.clientY,
            scrollTop: e.currentTarget.scrollTop,
        };
    }

    /**
     * Advances only on a genuine tap. Instruction screens scroll when the text
     * does not fit, so a pointer that travelled, that scrolled the screen, or
     * that was released while the screen was still moving must be treated as a
     * scroll gesture — otherwise a participant scrolling through the (longest)
     * first screen would skip past the instructions.
     */
    handleTapEnd = (advance, e) => {
        const origin = this.tapOrigin;
        this.tapOrigin = null;

        if (!origin) return;

        const moved = Math.abs(e.clientX - origin.x) > TAP_SLOP_PX
            || Math.abs(e.clientY - origin.y) > TAP_SLOP_PX;
        const scrolled = e.currentTarget.scrollTop !== origin.scrollTop
            || Date.now() - this.lastScrollAt < SCROLL_SETTLE_MS;

        if (moved || scrolled) return;

        advance();
    }

    /**
     * Reserves the height of the lesson's abort link, which the page header
     * draws above this task's fullscreen layer, so that text can never be cut
     * off underneath it. Deliberately not used on the trial screen, where the
     * stimuli must stay centred.
     */
    renderHeaderSpacer() {
        const { headerInset } = this.state;

        if (!headerInset) return null;

        return <div style={{ width: '100%', height: `${headerInset}px`, flexShrink: 0 }} />;
    }

    /**
     * A scrollable, tap-anywhere-to-continue screen.
     */
    renderTapScreen(advance, children) {
        return (
            <div
                className="flanker-container"
                style={styles.fullscreenTap}
                onScroll={this.handleScreenScroll}
                onPointerDown={this.handleTapStart}
                onPointerUp={e => this.handleTapEnd(advance, e)}>
                {this.renderHeaderSpacer()}
                <div style={styles.content}>
                    {children}
                </div>
            </div>
        );
    }

    advanceToPracticeIntro = () => {
        this.setState({ stage: 'practice_intro' });
    }

    renderIntro() {
        return this.renderTapScreen(this.advanceToPracticeIntro, (
            <React.Fragment>
                <p style={styles.instructionText}>{IntroSequenceText}</p>
                <p style={styles.instructionText}>{emphasize(IntroFocusText, IntroFocusEmphasis)}</p>
                <p style={styles.instructionText}>{IntroButtonsText}</p>
                <p style={styles.instructionText}>{IntroDifficultyText}</p>
                <p style={styles.instructionText}>{IntroSpeedText}</p>
                <div className="flanker-tap-prompt" style={styles.tapPrompt}>{IntroContinuePrompt}</div>
            </React.Fragment>
        ));
    }

    renderPracticeIntro() {
        return this.renderTapScreen(this.startPractice, (
            <React.Fragment>
                <p style={styles.instructionText}>{PracticeIntroText}</p>
                <p style={styles.instructionText}>{PracticeCheckText}</p>
                <div className="flanker-tap-prompt" style={styles.tapPrompt}>{PracticeStartPrompt}</div>
            </React.Fragment>
        ));
    }

    renderMainIntro() {
        return this.renderTapScreen(this.startMain, (
            <React.Fragment>
                <p style={styles.instructionText}>{MainIntroText}</p>
                <p style={styles.instructionText}>{MainRemindText}</p>
                <p style={styles.instructionText}>{MainProbesText}</p>
                <div className="flanker-tap-prompt" style={styles.tapPrompt}>{MainStartPrompt}</div>
            </React.Fragment>
        ));
    }

    renderRest() {
        return (
            <div className="flanker-container" style={styles.fullscreenScroll} onScroll={this.handleScreenScroll}>
                {this.renderHeaderSpacer()}
                <div style={styles.content}>
                    <h2 style={styles.heading}>{RestHeading}</h2>
                    <p style={styles.instructionText}>{RestText}</p>
                    <p style={styles.instructionText}>
                        {emphasize(
                            RestCountdownText.interpolate({ seconds: this.state.countdown }),
                            String(this.state.countdown),
                        )}
                    </p>
                </div>
            </div>
        );
    }

    handleSliderChange = e => {
        this.setState({
            sliderValue: parseInt(e.target.value, 10),
            sliderTouched: true,
        });
    }

    renderProbes() {
        const { probeIndex, timerLeft, sliderValue, sliderTouched } = this.state;

        return (
            <div className="flanker-container" style={styles.fullscreenScroll} onScroll={this.handleScreenScroll}>
                <style>{`
                    .flanker-slider-wrapper {
                        position: relative;
                        width: 100%;
                        max-width: 500px;
                        margin: 40px 0;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                    }
                    .flanker-slider-container {
                        position: relative;
                        width: 100%;
                        height: 40px;
                        display: flex;
                        align-items: center;
                    }
                    .flanker-slider-input {
                        width: 100%;
                        height: 8px;
                        background: #444;
                        border-radius: 4px;
                        outline: none;
                        -webkit-appearance: none;
                        appearance: none;
                        position: relative;
                        z-index: 2;
                    }
                    .flanker-slider-input::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        appearance: none;
                        width: 28px;
                        height: 28px;
                        border-radius: 50%;
                        background: ${SLIDER_THUMB_COLOR};
                        cursor: pointer;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.5);
                        border: none;
                    }
                    .flanker-slider-input::-moz-range-thumb {
                        width: 28px;
                        height: 28px;
                        border-radius: 50%;
                        background: ${SLIDER_THUMB_COLOR};
                        cursor: pointer;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.5);
                        border: none;
                    }
                    /* Until the participant moves it, the handle sits in the
                       middle in the lighter shade - the same cue the
                       questionnaire sliders give. */
                    .flanker-slider-input.untouched::-webkit-slider-thumb {
                        background: ${SLIDER_THUMB_COLOR_UNTOUCHED};
                    }
                    .flanker-slider-input.untouched::-moz-range-thumb {
                        background: ${SLIDER_THUMB_COLOR_UNTOUCHED};
                    }
                    .flanker-slider-ticks-lines {
                        display: flex;
                        justify-content: space-between;
                        width: 100%;
                        padding: 0 13px;
                        box-sizing: border-box;
                        position: absolute;
                        top: 50%;
                        left: 0;
                        transform: translateY(-50%);
                        pointer-events: none;
                        z-index: 3;
                    }
                    .tick-line {
                        width: 2px;
                        height: 18px;
                        background-color: #888;
                    }
                    .flanker-slider-labels {
                        display: flex;
                        justify-content: space-between;
                        width: 100%;
                        color: #fff;
                        font-size: 14px;
                        margin-top: 10px;
                    }
                    .flanker-slider-ticks {
                        display: flex;
                        justify-content: space-between;
                        width: 100%;
                        color: #fff;
                        font-size: 16px;
                        font-weight: bold;
                        margin-bottom: 10px;
                        padding: 0 8px;
                        box-sizing: border-box;
                    }
                `}</style>
                <div style={{ position: 'fixed', top: 0, left: 0, height: '5px', backgroundColor: '#4caf50', width: `${(timerLeft / PROBE_SECONDS) * 100}%`, transition: 'width 1s linear' }} />

                {this.renderHeaderSpacer()}
                <div style={styles.content}>
                    <h3 className="flanker-probe-question" style={{ fontSize: '24px', lineHeight: '1.4', color: '#fff', margin: '0 0 20px 0' }}>
                        {ProbeQuestions[probeIndex]}
                    </h3>

                    <div className="flanker-slider-wrapper">
                        <div className="flanker-slider-ticks">
                            <span>{'0'}</span>
                            <span>{'6'}</span>
                        </div>
                        <div className="flanker-slider-container">
                            <div className="flanker-slider-ticks-lines">
                                {[0, 1, 2, 3, 4, 5, 6].map(tick => (
                                    <span key={tick} className="tick-line" />
                                ))}
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="6"
                                step="1"
                                value={sliderValue}
                                onChange={this.handleSliderChange}
                                className={`flanker-slider-input ${!sliderTouched ? 'untouched' : ''}`}
                            />
                        </div>
                        <div className="flanker-slider-labels">
                            <span>{ProbeScaleMinLabel}</span>
                            <span>{ProbeScaleMaxLabel}</span>
                        </div>
                    </div>

                    {sliderTouched ? (
                        <button
                            className="btn waves-effect waves-light"
                            style={{ marginTop: '20px', backgroundColor: '#fff', color: '#000', alignSelf: 'center', fontWeight: 'bold' }}
                            onClick={() => this.handleProbeResponse(sliderValue)}>
                            {ProbeConfirmLabel}
                        </button>
                    ) : (
                        <div style={{ height: '56px' }} />
                    )}
                </div>
            </div>
        );
    }

    renderCompleted() {
        return (
            <div className="flanker-container" style={styles.fullscreenScroll} onScroll={this.handleScreenScroll}>
                {this.renderHeaderSpacer()}
                <div style={styles.content}>
                    <h2 style={styles.heading}>{CompletedHeading}</h2>
                    <p style={styles.instructionText}>{CompletedText}</p>
                    <button className="btn waves-effect waves-light" style={{ ...styles.btn, backgroundColor: '#4caf50' }} onClick={this.props.onContinue}>{CompletedContinueLabel}</button>
                </div>
            </div>
        );
    }

    renderTrial() {
        const { currentTrialPhase, currentTrial, pressedResponse } = this.state;

        return (
            <div className="flanker-container" style={styles.fullscreen}>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    {currentTrialPhase === 'fixation' && (
                        <div style={{ color: '#fff', fontSize: '60px' }}>{'+'}</div>
                    )}
                    {currentTrialPhase === 'stimulus' && currentTrial && (
                        this.renderStimulus(currentTrial)
                    )}
                    {currentTrialPhase === 'response' && (
                        <div style={{ width: '100%', height: '100%' }} />
                    )}
                </div>

                <div style={{ height: '120px', display: 'flex', width: '100%', padding: '10px', gap: '10px' }}>
                    {['left', 'right'].map(side => (
                        <div
                            key={side}
                            style={{
                                ...styles.responseButton,
                                backgroundColor: pressedResponse === side
                                    ? RESPONSE_BUTTON_COLOR_PRESSED
                                    : RESPONSE_BUTTON_COLOR,
                            }}
                            onPointerDown={() => {
                                this.handleResponse(side);
                            }}>
                            {side === 'left' ? '<' : '>'}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderLandscapeOverlay() {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#000',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#fff',
                fontFamily: 'Roboto, sans-serif',
                padding: '40px',
                textAlign: 'center',
            }}>
                <div style={{ fontSize: '60px', marginBottom: '30px', animation: 'flankerRotate 2s ease-in-out infinite' }}>
                    {'↻'}
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '15px' }}>{RotateHeading}</h2>
                <p style={{ fontSize: '16px', color: '#ccc', maxWidth: '300px' }}>{RotateText}</p>
                <style>{`
                    @keyframes flankerRotate {
                        0%, 100% { transform: rotate(0deg); }
                        50% { transform: rotate(90deg); }
                    }
                `}</style>
            </div>
        );
    }

    render() {
        const { stage, isPortrait } = this.state;

        if (isPortrait) return this.renderLandscapeOverlay();

        return (
            <div>
                <style>{`
                    @keyframes flankerPulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.4; }
                    }
                    /* Respect the iOS notch / home indicator in landscape mode. */
                    .flanker-container {
                        padding-top: max(20px, env(safe-area-inset-top, 0px)) !important;
                        padding-bottom: max(20px, env(safe-area-inset-bottom, 0px)) !important;
                        padding-left: max(20px, env(safe-area-inset-left, 0px)) !important;
                        padding-right: max(20px, env(safe-area-inset-right, 0px)) !important;
                    }
                    /* Compact layout for small landscape screens (phones), so
                       instructions and probe questions are not cut off. */
                    @media (max-height: 500px) {
                        .flanker-container {
                            padding-top: max(10px, env(safe-area-inset-top, 0px)) !important;
                            padding-bottom: max(10px, env(safe-area-inset-bottom, 0px)) !important;
                        }
                        .flanker-container p {
                            font-size: 14px !important;
                            line-height: 1.45 !important;
                        }
                        .flanker-container h2,
                        .flanker-container h3,
                        .flanker-probe-question {
                            font-size: 18px !important;
                            margin: 0 0 8px 0 !important;
                        }
                        .flanker-tap-prompt {
                            font-size: 16px !important;
                            margin-top: 12px !important;
                        }
                        .flanker-slider-wrapper {
                            margin: 14px 0 !important;
                        }
                    }
                `}</style>
                {stage === 'intro' && this.renderIntro()}
                {stage === 'practice_intro' && this.renderPracticeIntro()}
                {(stage === 'practice' || stage === 'main') && this.renderTrial()}
                {stage === 'practice_probes' && this.renderProbes()}
                {stage === 'main_intro' && this.renderMainIntro()}
                {stage === 'rest' && this.renderRest()}
                {stage === 'probes' && this.renderProbes()}
                {stage === 'completed' && this.renderCompleted()}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./scripts/frontend/lessons/components/Flanker.js